import type { Order } from "@/mocks/branch/orders";
import { PAYMENT_METHODS, PICKUP_METHODS, SOURCE_LABELS, vatBreakdown, hasAppFee, statusLabelOf } from "./constants";
import { mockBranchInfo } from "@/mocks/branch/employee";
import { buildZatcaTlvBase64, buildOrderTimestamp, generateQrDataUrl } from "@/utils/zatcaQr";

export async function printOrderReceipt(order: Order): Promise<void> {
  const { net, vat } = vatBreakdown(order.total);
  const qrText = buildZatcaTlvBase64({
    sellerName: mockBranchInfo.commercialName,
    vatNumber: mockBranchInfo.taxNumber,
    timestamp: buildOrderTimestamp(order.createdAt),
    totalWithVat: order.total,
    vatTotal: vat,
  });
  const qrUrl = await generateQrDataUrl(qrText);

  const w = window.open("", "_blank", "width=360,height=720");
  if (!w) return;

  const status = statusLabelOf(order, order.status);
  const pickup = PICKUP_METHODS[order.pickupMethod].label;
  const payment = PAYMENT_METHODS[order.paymentMethod].label;
  const source = SOURCE_LABELS[order.source].label;

  const qrBlock = qrUrl
    ? `<img class="qr-img" src="${qrUrl}" alt="رمز QR للفاتورة الضريبية" />`
    : `<div class="qr">رمز QR غير متاح</div>`;

  const itemsRows = order.items
    .map(
      (item) => `
      <div style="display:flex;justify-content:space-between;padding:3px 0;font-size:11px;border-bottom:1px dotted #ddd;">
        <span style="flex:1;">${item.quantity} × ${item.name}</span>
        <span style="white-space:nowrap;">${(item.quantity * item.unitPrice).toFixed(2)}</span>
      </div>`,
    )
    .join("");

  const row = (label: string, value: string, cls = "") =>
    `<div class="row ${cls}"><span>${label}</span><span>${value}</span></div>`;

  const html = `<!DOCTYPE html>
<html lang="ar" dir="rtl">
<head>
<meta charset="utf-8" />
<title>فاتورة ضريبية #${order.number}</title>
<style>
  * { margin:0; padding:0; box-sizing:border-box; }
  body { font-family:'Graphik Arabic','IBM Plex Sans Arabic','Segoe UI',sans-serif; width:80mm; margin:0 auto; padding:10px; color:#1e1e1e; }
  .center { text-align:center; }
  .bold { font-weight:700; }
  .muted { color:#6b7280; font-size:10px; }
  .title { font-size:16px; margin:4px 0; }
  .divider { border-top:1px dashed #999; margin:8px 0; }
  .row { display:flex; justify-content:space-between; font-size:11px; padding:2px 0; }
  .total { font-size:14px; font-weight:700; }
  .badge { display:inline-block; border:1px solid #2E9E46; color:#2E9E46; border-radius:8px; font-size:9px; padding:2px 6px; margin-top:4px; }
  .qr { width:110px; height:110px; border:1px solid #999; margin:10px auto; display:flex; flex-direction:column; align-items:center; justify-content:center; text-align:center; font-size:9px; color:#6b7280; }
  .qr-img { width:120px; height:120px; margin:10px auto; display:block; }
  .footer { text-align:center; font-size:10px; color:#6b7280; margin-top:8px; line-height:1.6; }
</style>
</head>
<body>
  <div class="center">
    <div class="bold title">${mockBranchInfo.commercialName}</div>
    <div class="muted">${mockBranchInfo.branchName} — ${mockBranchInfo.address}</div>
    <div class="muted">الرقم الضريبي: ${mockBranchInfo.taxNumber}</div>
    <div class="badge">فاتورة ضريبية مبسطة</div>
  </div>
  <div class="divider"></div>
  ${row("رقم الفاتورة", `INV-${order.number}`, "bold")}
  ${row("الطلب", `#${order.number}`)}
  ${row("التاريخ والوقت", order.createdAt, "muted")}
  ${row("الحالة", status)}
  ${row("المصدر", source)}
  ${row("الاستلام", pickup)}
  ${row("الدفع", payment)}
  <div class="divider"></div>
  ${itemsRows}
  <div class="divider"></div>
  ${row("المجموع الفرعي", order.subtotal.toFixed(2))}
  ${order.discount > 0 ? row("الخصم", `-${order.discount.toFixed(2)}`) : ""}
  ${hasAppFee(order) ? row("رسوم التطبيق", order.appFee.toFixed(2)) : ""}
  ${order.deliveryFee && order.deliveryFee > 0 ? row("رسوم التوصيل", order.deliveryFee.toFixed(2)) : ""}
  ${row("صافي قبل الضريبة", net.toFixed(2))}
  ${row("الضريبة المتضمنة 15%", vat.toFixed(2))}
  <div class="divider"></div>
  <div class="row total"><span>الإجمالي شامل الضريبة</span><span>${order.total.toFixed(2)} ر.س</span></div>
  <div class="divider"></div>
  ${qrBlock}
  <div class="muted center" style="margin-top:4px;">رمز QR بصيغة TLV / Base64 وفق متطلبات فاتورة</div>
  <div class="footer">فاتورة ضريبية مبسطة وفق البيانات المعتمدة<br/>شكراً لزيارتكم — Your Cashier</div>
</body>
</html>`;

  w.document.open();
  w.document.write(html);
  w.document.close();
  window.setTimeout(() => {
    w.focus();
    w.print();
  }, 300);
}